import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../utils/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users,
  TrendingUp,
  Calendar,
  Video,
  Edit,
  Target,
  ArrowRight,
  CheckCircle2,
  Plug,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Each stat card now includes an `href` for navigation and a `gradient` for accent color
const statConfig = {
  owner: [
    {
      key: "total_leads",
      label: "Total Leads",
      subKey: "new_leads",
      subLabel: "new this month",
      icon: Users,
      href: "/leads",
      accent: "#3B82F6", // blue
      lightBg: "#EFF6FF",
      iconColor: "#1D4ED8",
    },
    {
      key: "active_clients",
      label: "Active Clients",
      sub: "Currently active",
      icon: TrendingUp,
      href: "/clients",
      accent: "#10B981", // emerald
      lightBg: "#ECFDF5",
      iconColor: "#065F46",
    },
    {
      key: "upcoming_shoots",
      label: "Upcoming Shoots",
      sub: "Scheduled",
      icon: Video,
      href: "/shoots",
      accent: "#8B5CF6", // violet
      lightBg: "#F5F3FF",
      iconColor: "#5B21B6",
    },
    {
      key: "pending_editing",
      label: "Pending Editing",
      sub: "Awaiting editor",
      icon: Edit,
      href: "/editing",
      accent: "#F59E0B", // amber
      lightBg: "#FFFBEB",
      iconColor: "#92400E",
    },
    {
      key: "live_ads",
      label: "Live Ads",
      sub: "Currently running",
      icon: Target,
      href: "/ads",
      accent: "#EF4444", // rose
      lightBg: "#FEF2F2",
      iconColor: "#991B1B",
    },
  ],
  pillar: [
    {
      key: "assigned_leads",
      label: "Assigned Leads",
      sub: "Your pipeline",
      icon: Users,
      href: "/leads",
      accent: "#3B82F6",
      lightBg: "#EFF6FF",
      iconColor: "#1D4ED8",
    },
    {
      key: "my_clients",
      label: "My Clients",
      sub: "Active accounts",
      icon: TrendingUp,
      href: "/clients",
      accent: "#10B981",
      lightBg: "#ECFDF5",
      iconColor: "#065F46",
    },
    {
      key: "scheduled_shoots",
      label: "Scheduled Shoots",
      sub: "Upcoming",
      icon: Video,
      href: "/shoots",
      accent: "#8B5CF6",
      lightBg: "#F5F3FF",
      iconColor: "#5B21B6",
    },
  ],
  employee: [
    {
      key: "my_editing_tasks",
      label: "Editing Tasks",
      sub: "Assigned to you",
      icon: Edit,
      href: "/editing",
      accent: "#F59E0B",
      lightBg: "#FFFBEB",
      iconColor: "#92400E",
    },
    {
      key: "pending_tasks",
      label: "Pending Tasks",
      sub: "Need action",
      icon: Edit,
      href: "/editing",
      accent: "#EF4444",
      lightBg: "#FEF2F2",
      iconColor: "#991B1B",
    },
    {
      key: "my_ads",
      label: "My Ads",
      sub: "Assigned campaigns",
      icon: Target,
      href: "/ads",
      accent: "#3B82F6",
      lightBg: "#EFF6FF",
      iconColor: "#1D4ED8",
    },
  ],
};

const roleLabel = {
  owner: "Full system overview — all agencies at a glance",
  pillar: "Your assigned leads and client pipeline",
  employee: "Your tasks, ads, and assignments",
};

const quickActions = {
  owner: [
    { label: "Add New Lead", href: "/leads", icon: Users },
    { label: "Schedule Meeting", href: "/meetings", icon: Calendar },
    { label: "Plan Shoot", href: "/shoots", icon: Video },
  ],
  pillar: [
    { label: "Add New Lead", href: "/leads", icon: Users },
    { label: "Schedule Meeting", href: "/meetings", icon: Calendar },
    { label: "Plan Shoot", href: "/shoots", icon: Video },
  ],
  employee: [
    { label: "View My Tasks", href: "/editing", icon: Edit },
    { label: "My Ads", href: "/ads", icon: Target },
  ],
};

function StatCardSkeleton() {
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <Skeleton className="h-4 w-4 rounded" />
        </div>
        <Skeleton className="h-8 w-20 mb-2" />
        <Skeleton className="h-4 w-28 mb-1" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  );
}

function StatCard({ cardConfig, value, subValue, onClick }) {
  const {
    label,
    sub,
    subKey,
    subLabel,
    icon: Icon,
    accent,
    lightBg,
    iconColor,
  } = cardConfig;

  return (
    <button
      onClick={onClick}
      className="group w-full text-left rounded-2xl border border-border bg-card overflow-hidden hover:border-transparent hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      style={{
        "--card-accent": accent,
        "--card-light-bg": lightBg,
        "--card-icon-color": iconColor,
      }}
    >
      {/* Accent top bar */}
      <div className="h-1 w-full" style={{ background: accent }} />

      <div className="p-5">
        {/* Icon row */}
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
            style={{ background: lightBg }}
          >
            <Icon className="w-5 h-5" style={{ color: iconColor }} />
          </div>
          <ArrowUpRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200" />
        </div>

        {/* Value */}
        <p
          className="text-3xl font-bold tracking-tight mb-1 transition-colors duration-200"
          style={{
            color: "var(--foreground)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value ?? 0}
        </p>

        {/* Label */}
        <p className="text-sm font-medium text-foreground mb-1">{label}</p>

        {/* Sub-label */}
        <p className="text-xs text-muted-foreground">
          {subKey ? (
            <>
              <span className="font-semibold" style={{ color: accent }}>
                +{subValue ?? 0}
              </span>{" "}
              {subLabel}
            </>
          ) : (
            sub
          )}
        </p>
      </div>
    </button>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [calendarConnected, setCalendarConnected] = useState(false);

  useEffect(() => {
    fetchStats();
    checkCalendarStatus();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get("/stats");
      setStats(response.data);
    } catch {
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  const checkCalendarStatus = async () => {
    try {
      const response = await apiClient.get("/calendar/status");
      setCalendarConnected(response.data.connected);
    } catch {
      console.error("Failed to check calendar status");
    }
  };

  const connectCalendar = async () => {
    try {
      const response = await apiClient.get("/calendar/connect");
      if (response.data.authorization_url)
        window.location.href = response.data.authorization_url;
    } catch {
      toast.error("Failed to connect Google Calendar");
    }
  };
  const disconnectCalendar = async () => {
    try {
      await apiClient.post("/calendar/disconnect");
      setCalendarConnected(false);
      toast.success("Google Calendar disconnected");
    } catch {
      toast.error("Failed to disconnect Google Calendar");
    }
  };

  const role = user?.role ?? "employee";
  const cards = statConfig[role] ?? statConfig.employee;
  const actions = quickActions[role] ?? quickActions.employee;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* ── Welcome Banner ── */}
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/70 p-6 lg:p-8 text-white shadow-lg">
        <p className="text-white/70 text-sm font-medium uppercase tracking-widest mb-1">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </p>
        <h1
          className="text-3xl lg:text-4xl font-bold mb-1"
          style={{ letterSpacing: "-0.02em" }}
        >
          Welcome back, {user?.name?.split(" ")[0]} 👋
        </h1>
        <p className="text-white/75 text-sm">
          {roleLabel[role] ?? "Your workspace"}
        </p>
      </div>

      {/* ── Stat Cards ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
            Overview
          </h2>
          <span className="text-xs text-muted-foreground">
            Click any card to navigate
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading
            ? cards.map((_, i) => <StatCardSkeleton key={i} />)
            : cards.map((card) => (
                <StatCard
                  key={card.key}
                  cardConfig={card}
                  value={stats[card.key]}
                  subValue={card.subKey ? stats[card.subKey] : undefined}
                  onClick={() => navigate(card.href)}
                />
              ))}
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <div>
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {actions.map(({ label, href, icon: Icon }) => (
            <button
              key={href}
              onClick={() => navigate(href)}
              className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3.5 text-left hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm transition-all duration-150"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors duration-150">
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium">{label}</span>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {/* ── Integrations ── */}
      <div>
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
          Integrations
        </h2>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-white border border-border shadow-sm flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-sm">Google Calendar</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {calendarConnected
                      ? "Syncing meetings and shoots"
                      : "Auto-sync meetings and shoots"}
                  </p>
                </div>
              </div>
              {calendarConnected ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 text-sm text-emerald-600 font-medium bg-emerald-50 px-3 py-1.5 rounded-full">
                    <CheckCircle2 className="w-4 h-4" />
                    Connected
                  </div>
                  <Button
                    onClick={disconnectCalendar}
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    data-testid="disconnect-calendar-button"
                  >
                    Disconnect
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={connectCalendar}
                  size="sm"
                  data-testid="connect-calendar-button"
                >
                  <Plug className="w-3.5 h-3.5 mr-2" />
                  Connect
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
